import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import "./ProfileSettings.css";
import { profilePic } from "../../../../icons/assets";
import { getProfile, updateProfile } from "../../../../services/api/app/user";
import { getStatus as getSubscriptionStatus } from "../../../../services/api/app/subscriptions";
import logger from "../../../../utils/logger";
import { mapProductPlanName } from "../../../../utils/planNames";
import {
  DEFAULT_DISPLAY_NAME_FORMAT,
  DISPLAY_NAME_FORMAT_META_KEY,
  DISPLAY_NAME_FORMATS,
  formatDisplayName,
  getDisplayNameFormatFromUser,
  normalizeDisplayNameFormat,
  previewDisplayNameFormat,
} from "../../../../utils/displayName";

const normalizeProfile = (data) => {
  if (!data) return null;
  if (data.profile && typeof data.profile === "object") return data.profile;
  return data;
};

const resolvePlanInfo = (profile, benefits) => {
  if (benefits?.has_subscription) {
    return {
      label: mapProductPlanName(
        benefits.plan_name || "Subscribed",
        benefits.monthly_credits
      ),
      variant: "pro",
      hasSubscription: true,
      renewsAt: benefits.end_date || null,
      autoRenewal: benefits.auto_renewal !== false,
    };
  }

  if (benefits?.plan_name) {
    return {
      label: benefits.plan_name,
      variant: benefits.plan_name === "Free" ? "free" : "pro",
      hasSubscription: false,
      renewsAt: benefits.renew_by_date || null,
      autoRenewal: false,
      accessPhase: benefits.access_phase || "free",
      previousPlanName: benefits.previous_plan_name || null,
      lockedCredits: Number(
        benefits.locked_subscription_credits ??
          benefits.retained_subscription_credits ??
          0
      ),
    };
  }

  if (profile?.subscription_status === "active" && profile?.plan_type) {
    return {
      label: mapProductPlanName(profile.plan_type),
      variant: "pro",
      hasSubscription: true,
      renewsAt: null,
      autoRenewal: true,
    };
  }

  return {
    label: "Pay as you go",
    variant: "free",
    hasSubscription: false,
    renewsAt: null,
    autoRenewal: false,
    accessPhase: "payg",
    previousPlanName: null,
    lockedCredits: 0,
  };
};

const formatPlanDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const ProfileSettings = ({ onProfileUpdated, onChangePlan }) => {
  const { getToken } = useAuth();
  const { user } = useUser();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [avatarUrl, setAvatarUrl] = useState(profilePic);
  const [planInfo, setPlanInfo] = useState(() => resolvePlanInfo(null, null));
  const [nameFormat, setNameFormat] = useState(DEFAULT_DISPLAY_NAME_FORMAT);
  const [initialNames, setInitialNames] = useState({
    first: "",
    last: "",
    format: DEFAULT_DISPLAY_NAME_FORMAT,
  });

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError("");
    setStatusMessage("");

    const clerkFirst = user?.firstName || "";
    const clerkLast = user?.lastName || "";
    const clerkEmail = user?.primaryEmailAddress?.emailAddress || "";
    const clerkAvatar = user?.imageUrl || profilePic;
    const clerkFormat = getDisplayNameFormatFromUser(user);

    setFirstName(clerkFirst);
    setLastName(clerkLast);
    setEmail(clerkEmail);
    setAvatarUrl(clerkAvatar);
    setNameFormat(clerkFormat);
    setInitialNames({
      first: clerkFirst,
      last: clerkLast,
      format: clerkFormat,
    });

    try {
      const token = await getToken();
      if (!token) {
        setLoading(false);
        return;
      }

      const [profileRes, statusRes] = await Promise.allSettled([
        getProfile(token),
        getSubscriptionStatus(token),
      ]);

      let nextPlan = resolvePlanInfo(null, null);

      if (profileRes.status === "fulfilled") {
        const profile = normalizeProfile(profileRes.value);
        if (profile) {
          const first = profile.first_name ?? clerkFirst;
          const last = profile.last_name ?? clerkLast;
          setFirstName(first || "");
          setLastName(last || "");
          setEmail(profile.email || clerkEmail);
          setAvatarUrl(profile.profile_picture || clerkAvatar);
          setInitialNames({
            first: first || "",
            last: last || "",
            format: clerkFormat,
          });
          nextPlan = resolvePlanInfo(profile, null);
        }
      } else {
        logger.warn("Failed to load profile", profileRes.reason);
      }

      if (statusRes.status === "fulfilled" && statusRes.value?.success) {
        const benefits = statusRes.value.benefits;
        if (benefits) {
          nextPlan = resolvePlanInfo(null, benefits);
        }
      }

      setPlanInfo(nextPlan);
    } catch (err) {
      logger.error("Failed to load profile settings", err);
      setError("Couldn’t sync account details. Showing your signed-in info.");
    } finally {
      setLoading(false);
    }
  }, [getToken, user]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const displayName = useMemo(
    () =>
      formatDisplayName({
        firstName,
        lastName,
        format: nameFormat,
        fallback: email || "User",
      }),
    [firstName, lastName, nameFormat, email]
  );

  const isDirty =
    firstName.trim() !== initialNames.first.trim() ||
    lastName.trim() !== initialNames.last.trim() ||
    normalizeDisplayNameFormat(nameFormat) !==
      normalizeDisplayNameFormat(initialNames.format);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!isDirty || saving) return;

    setSaving(true);
    setError("");
    setStatusMessage("");

    const nextFirst = firstName.trim();
    const nextLast = lastName.trim();
    const nextFormat = normalizeDisplayNameFormat(nameFormat);

    try {
      if (user) {
        try {
          await user.update({
            firstName: nextFirst || undefined,
            lastName: nextLast || undefined,
            unsafeMetadata: {
              ...(user.unsafeMetadata || {}),
              [DISPLAY_NAME_FORMAT_META_KEY]: nextFormat,
            },
          });
        } catch (clerkErr) {
          logger.warn("Clerk profile update failed; updating backend only", clerkErr);
        }
      }

      const token = await getToken();
      if (token) {
        await updateProfile(token, {
          first_name: nextFirst,
          last_name: nextLast,
        });
      }

      setInitialNames({
        first: nextFirst,
        last: nextLast,
        format: nextFormat,
      });
      setNameFormat(nextFormat);
      setStatusMessage("Display preferences saved.");
      onProfileUpdated?.({
        firstName: nextFirst,
        lastName: nextLast,
        displayNameFormat: nextFormat,
      });
    } catch (err) {
      logger.error("Failed to save profile", err);
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Couldn’t save display preferences. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFirstName(initialNames.first);
    setLastName(initialNames.last);
    setNameFormat(initialNames.format);
    setError("");
    setStatusMessage("");
  };

  const renewLabel = formatPlanDate(planInfo.renewsAt);
  const planMetaText = planInfo.hasSubscription
    ? renewLabel
      ? planInfo.autoRenewal
        ? `Renews ${renewLabel}`
        : `Access until ${renewLabel}`
      : "Active subscription"
    : planInfo.accessPhase === "grace"
      ? renewLabel
        ? `Renew by ${renewLabel} to keep ${planInfo.lockedCredits} credits`
        : "Renew soon to keep your credits"
      : planInfo.accessPhase === "soft_locked"
        ? renewLabel
          ? `Renew by ${renewLabel} to unlock ${planInfo.lockedCredits} credits`
          : "Renew to unlock your credits"
        : planInfo.label === "Free"
          ? "No credits available"
          : "Usage based billing";

  const headerStatus = loading
    ? "Syncing account details…"
    : saving
      ? "Saving display preferences…"
      : error
        ? error
        : statusMessage || "";

  const headerStatusTone = error
    ? "error"
    : statusMessage && !loading && !saving
      ? "success"
      : "muted";

  return (
    <div className="profile-settings-container">
      <div className="profile-settings-container__header">
        <img className="profile-pic" src={avatarUrl || profilePic} alt="" />
        <div className="profile-name-section">
          <div className="profile-name-row">
            <p className="profile-name">{displayName}</p>
            <p
              className={`profile-header-status profile-header-status--${headerStatusTone}`}
              role="status"
              aria-live="polite"
            >
              {headerStatus || "\u00A0"}
            </p>
          </div>
          <div className="profile-plan-row">
            <span
              className={`profile-plan-badge profile-plan-badge--${planInfo.variant}`}
            >
              <span className="profile-plan-badge__dot" aria-hidden="true" />
              {planInfo.label}
            </span>
            <span className="profile-plan-meta">{planMetaText}</span>
            {onChangePlan && (
              <button
                type="button"
                className="profile-plan-upgrade"
                onClick={onChangePlan}
              >
                {planInfo.hasSubscription ? "Change plan" : "Upgrade"}
              </button>
            )}
          </div>
        </div>
      </div>

      <form className="profile-settings-container__form" onSubmit={handleSave}>
        <div className="name-details">
          <div className="input-field">
            <label htmlFor="profile-first-name">First Name</label>
            <input
              id="profile-first-name"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Enter first name"
              autoComplete="given-name"
              disabled={loading || saving}
            />
          </div>
          <div className="input-field">
            <label htmlFor="profile-last-name">Last Name</label>
            <input
              id="profile-last-name"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Enter last name"
              autoComplete="family-name"
              disabled={loading || saving}
            />
          </div>
        </div>

        <div className="input-field profile-display-format">
          <label htmlFor="profile-display-name-format">Show my name as</label>
          <select
            id="profile-display-name-format"
            value={nameFormat}
            onChange={(e) => setNameFormat(e.target.value)}
            disabled={loading || saving}
          >
            {DISPLAY_NAME_FORMATS.map((option) => (
              <option key={option.id} value={option.id}>
                {previewDisplayNameFormat(firstName, lastName, option.id) ||
                  option.label}
              </option>
            ))}
          </select>
          <p className="profile-field-hint profile-display-format__hint">
            Preview updates from your first and last name.
          </p>
        </div>

        <div className="email-details">
          <div className="input-field">
            <label htmlFor="profile-email">Email</label>
            <input
              id="profile-email"
              type="email"
              value={email}
              placeholder="No email on file"
              readOnly
              disabled
              title="Email is managed by your sign-in account"
            />
          </div>
        </div>
        <p className="profile-field-hint">
          Email is managed by your sign-in account and can’t be edited here.
        </p>

        <div className="profile-settings-actions">
          <button
            type="button"
            className="profile-settings-btn profile-settings-btn--ghost"
            onClick={handleReset}
            disabled={!isDirty || saving || loading}
          >
            Reset
          </button>
          <button
            type="submit"
            className="profile-settings-btn"
            disabled={!isDirty || saving || loading}
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfileSettings;
